import { useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Autocomplete, TextField, IconButton, Chip } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { StyledTableRow, StyledTableBody, StyledTableCell, 
  UXDataTableWithoutBody } from "../../../../components/UXDataTable";
import { GlobalSecondaryButton } from "../../../styles";
import CustomModal from "../../../../components/Modal";
import LocationForm from "./LocationForm";

const Header = styled(Box)(() => ({
  fontSize: "20px",
  marginLeft: 10,
  paddingRight: 20,
}));
 
export const StyledTableCellSmall = styled(StyledTableCell)({
  width: 30,
});

const colHeaders = [
  { id: 'locationName', label: 'Location Name' },
  { id: 'isActive', label: 'Active' },
  { id: 'canContainSamples', label: 'Contain Samples' },
  { id: 'isDeliveryLocation', label: 'Delivery Location' },
];

const LocationManagement = ({ siteData, allLocationsLoading, setSiteData, canEdit}) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const [filterLocationValue, setFilterLocationValue] = useState(null);
  const [locationData, setLocationData] = useState(null);

  const [showLocationFormModal, setShowLocationFormModal] = useState(false);

  const siteLocations = siteData?.locations ?? [];
  const filteredLocations = filterLocationValue ? 
    siteLocations.filter(l => l.locationName === filterLocationValue) : siteLocations;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function determineStartArray(arrayLocations) {
    if (arrayLocations) {
      return stableSort(arrayLocations, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    } else {
      return []
    }
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewLocationClick = () => {
    setLocationData(null);
    setShowLocationFormModal(true);
  };

  const handleEditLocationClick = (location) => {
    setLocationData(location);
    setShowLocationFormModal(true);
  };
  
  const handleLocationFormCancel = () => {
    setLocationData(null);
    setShowLocationFormModal(false);
  }

  const handleDeleteLocationClick = (location) => {
    if(!location.isNew) return;

    setSiteData(prev => ({
      ...prev,
      locations: prev.locations.filter(x => x.locationName !== location.locationName)
    }));
    setLocationData(null);
  }

  const handleLocationFormSave = (savedLocation, isAddLocation) => {
    if (isAddLocation) {
      setSiteData(prev => ({
        ...prev,
        locations: [savedLocation, ...prev.locations]
      }));
    }
    else {
      setSiteData(prev => ({
        ...prev,
        locations: prev.locations.map(x => x.locationName === savedLocation.locationName ? savedLocation : x)
      }));
    }
    setShowLocationFormModal(false);
  }

  const boolChip = (value) => {
    return (<Chip
      label={value ? "Yes" : "No"}
      color={value ? "success" : undefined}
      disabled={!value}
    />);
  } 

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop:'30px', padding: '0px',width: '1000px' }}>

      <Box display="inline-flex"  justifyContent={"space-between"} alignItems={"center"}>
        
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <Header>Site Locations ({siteLocations.length})</Header>
          <GlobalSecondaryButton
            variant="contained"
            type="button"
            onClick={() => handleNewLocationClick()}
            disabled={!canEdit || allLocationsLoading}>
            New Location
          </GlobalSecondaryButton>
        </Box>

        <Autocomplete
          noOptionsText={"No locations found"}
          value={filterLocationValue}
          style={{ marginRight: '10px' }}
          onChange={(e, value) => { 
            setFilterLocationValue(value);
            setPage(0);
          }}
          disablePortal
          options={siteLocations.map((l) => l.locationName)}
          sx={{ width: "20rem" }}
          renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Filter Location" />}
          autoSelect
        />

      </Box>

      <UXDataTableWithoutBody
        tableWidth='100%'
        cols={colHeaders}
        blankFirstHeader={true}
        tableId="siteLocationTable"
        rowLength={filteredLocations.length}
        enablePaging={true}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        noDataFoundMessage={'No locations found'}
        enableAddIcon={false}
        addFunction={null}
        addToolTipText={null}
        enableCheckbox={false}
        enableSorteable={true}
        order={order}
        orderBy={orderBy}
        handleRequestSort={handleRequestSort}
      >
        <StyledTableBody key={"myTablebody"}>
          {determineStartArray(filteredLocations).map((location, rowIndex) => {
            return (
              <StyledTableRow
                hover
                key={`custom-row-${rowIndex}`}> 

                <StyledTableCellSmall>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <IconButton color="error" style={{ padding: 0 }}
                      disabled={!canEdit || !location.isNew}              
                    >
                      <Delete fontSize="small" color="inherit"
                        onClick={() => handleDeleteLocationClick(location)}
                      />
                    </IconButton>

                    <IconButton color="info" style={{ padding: 0 }}
                        disabled={!canEdit}                                              
                    >
                        <Edit fontSize="small" color="inherit"
                          onClick={() => handleEditLocationClick(location)}
                        />
                    </IconButton>
                  </Box>
                </StyledTableCellSmall>

                <StyledTableCell style={{ width: '100px' }}>{location.locationName}</StyledTableCell>    
                <StyledTableCell style={{ width: '100px' }}>{boolChip(location.isActive)}</StyledTableCell>
                <StyledTableCell style={{ width: '50px' }}>{boolChip(location.canContainSamples)}</StyledTableCell>  
                <StyledTableCell style={{ width: '50px' }}>{boolChip(location.isDeliveryLocation)}</StyledTableCell>
              </StyledTableRow>
            );
          })}
        </StyledTableBody>
      </UXDataTableWithoutBody>

      <CustomModal
        title={locationData?.locationName ? "Edit Location" : "Add New Location" }
        open={showLocationFormModal}
        setOpen={setShowLocationFormModal}
        setClose={undefined}>
        <LocationForm
          locationData={locationData}
          siteData={siteData}
          onSave={handleLocationFormSave} 
          onCancel={handleLocationFormCancel}>
        </LocationForm>        
      </CustomModal>

    </Box>
  );
};

export default LocationManagement;
